import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import type { CustomNode } from '../types';
import { buildNodeMetadata, metadataFileName } from '../utils/nodeMetadata';
import '../styles/custom-nodes.css';

function formatBytes(bytes: number): string {
  if (!bytes) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, exponent);
  return `${value.toFixed(value >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function CustomNodeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [node, setNode] = useState<CustomNode | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!id) {
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    api
      .customNode(id)
      .then((result) => {
        if (active) {
          setNode(result);
        }
      })
      .catch((err) => {
        if (active) {
          setError((err as Error).message);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [id]);

  const handleDownload = async () => {
    if (!node) {
      return;
    }
    setDownloading(true);
    setError(null);
    try {
      const { blob, filename } = await api.downloadCustomNode(node.id);
      saveBlob(blob, filename);
      setNode((current) =>
        current ? { ...current, downloadCount: current.downloadCount + 1 } : current
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadMetadata = () => {
    if (!node) {
      return;
    }
    const blob = new Blob([JSON.stringify(buildNodeMetadata(node), null, 2)], {
      type: 'application/json'
    });
    saveBlob(blob, metadataFileName(node));
  };

  const handleCopyConfig = async () => {
    if (!node?.configExample) {
      return;
    }
    try {
      await navigator.clipboard.writeText(node.configExample);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  const handleDelete = async () => {
    if (!node) {
      return;
    }
    if (!window.confirm(`Delete "${node.name}"? This removes the bundle and cannot be undone.`)) {
      return;
    }
    setDeleting(true);
    setError(null);
    try {
      await api.deleteCustomNode(node.id);
      navigate('/custom-nodes');
    } catch (err) {
      setError((err as Error).message);
      setDeleting(false);
    }
  };

  const renderRequired = (required?: string) => {
    const value = (required || '').trim();
    if (/^yes$/i.test(value)) {
      return <span className="cn-req cn-req--yes">Required</span>;
    }
    if (!value || /^no$/i.test(value)) {
      return <span className="cn-req cn-req--no">Optional</span>;
    }
    return <span className="cn-req cn-req--cond">{value}</span>;
  };

  if (loading) {
    return (
      <div className="custom-node-detail">
        <div className="loading">Loading custom node…</div>
      </div>
    );
  }

  if (error && !node) {
    return (
      <div className="custom-node-detail">
        <div className="alert alert-error">{error}</div>
        <Link to="/custom-nodes" className="button button-tertiary">
          Back to Custom Nodes
        </Link>
      </div>
    );
  }

  if (!node) {
    return null;
  }

  return (
    <div className="custom-node-detail">
      <div className="custom-node-detail__breadcrumb">
        <Link to="/custom-nodes">
          <span className="material-icons">arrow_back</span>
          Custom Nodes
        </Link>
      </div>

      {error ? <div className="alert alert-error">{error}</div> : null}

      <div className="cn-detail-header card">
        <div className="cn-detail-header__icon">
          {node.iconDataUrl ? (
            <img src={node.iconDataUrl} alt="" />
          ) : (
            <span className="material-icons">extension</span>
          )}
        </div>
        <div className="cn-detail-header__main">
          <div className="cn-detail-header__title">
            <h1>{node.name}</h1>
            <span
              className={`status ${node.visibility === 'public' ? 'status-approved' : 'status-pending'}`}
            >
              {node.visibility === 'public' ? 'Everyone' : 'Selected teams'}
            </span>
          </div>
          <p className="cn-detail-header__bundle">
            <code>{node.bundleName}</code> · v{node.bundleVersion}
            {node.nodeName ? (
              <>
                {' '}
                · class <code>{node.nodeName}</code>
              </>
            ) : null}
            {node.category ? <span className="cn-chip">{node.category}</span> : null}
          </p>
          {node.description ? <p className="cn-detail-header__desc">{node.description}</p> : null}
          <div className="cn-detail-header__actions">
            <button className="button button-primary" onClick={handleDownload} disabled={downloading || !node.file}>
              <span className="material-icons">download</span>
              {downloading ? 'Preparing…' : 'Download .tgz'}
            </button>
            <button className="button button-secondary" onClick={handleDownloadMetadata}>
              <span className="material-icons">data_object</span>
              Download metadata.json
            </button>
            {node.canManage ? (
              <>
                <Link to={`/custom-nodes/${node.id}/edit`} className="button button-tertiary">
                  <span className="material-icons">edit</span>
                  Edit
                </Link>
                <button className="button button-danger" onClick={handleDelete} disabled={deleting}>
                  <span className="material-icons">delete</span>
                  {deleting ? 'Deleting…' : 'Delete'}
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>

      <div className="cn-detail-grid">
        <div className="cn-detail-main">
          <section className="card">
            <h2>Configuration</h2>
            {node.configFields && node.configFields.length > 0 ? (
              <div className="cn-fields-wrap">
                <table className="cn-fields-table">
                  <thead>
                    <tr>
                      <th>Key</th>
                      <th>Type</th>
                      <th>Required</th>
                      <th>Default</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {node.configFields.map((field) => (
                      <tr key={field.key}>
                        <td>
                          <code>{field.key}</code>
                        </td>
                        <td className="cn-fields-type">{field.type || '—'}</td>
                        <td>{renderRequired(field.required)}</td>
                        <td>
                          {field.default ? <code>{field.default}</code> : <span className="cn-muted">—</span>}
                        </td>
                        <td className="cn-fields-notes">{field.notes || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="hint">This node takes no configuration.</p>
            )}

            {node.configExample ? (
              <div className="cn-example">
                <div className="cn-section-header">
                  <h3>Example</h3>
                  <button className="button button-tertiary button-small" onClick={handleCopyConfig}>
                    <span className="material-icons">{copied ? 'check' : 'content_copy'}</span>
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <pre className="cn-code">{node.configExample}</pre>
              </div>
            ) : null}
          </section>

          <section className="card">
            <h2>Node metadata</h2>
            <p className="hint">
              Use these values to register the node in IO under <strong>Flow Nodes → Metadata</strong>.
            </p>
            <dl className="cn-meta-grid">
              <div>
                <dt>Display name</dt>
                <dd>{node.name || '—'}</dd>
              </div>
              <div>
                <dt>Bundle name</dt>
                <dd>{node.bundleName ? <code>{node.bundleName}</code> : '—'}</dd>
              </div>
              <div>
                <dt>Bundle version</dt>
                <dd>{node.bundleVersion || '—'}</dd>
              </div>
              <div>
                <dt>Node class</dt>
                <dd>{node.nodeName ? <code>{node.nodeName}</code> : '—'}</dd>
              </div>
              <div>
                <dt>Category</dt>
                <dd>{node.category ? <code>{node.category}</code> : '—'}</dd>
              </div>
              <div>
                <dt>Additional outputs</dt>
                <dd>{node.allowAdditionalOutputs ? 'Allowed' : 'Fixed'}</dd>
              </div>
            </dl>

            <div className="cn-io">
              <div>
                <h4>Inputs</h4>
                <ul className="cn-io-list">
                  {(node.inputs && node.inputs.length > 0 ? node.inputs : [{ name: 'input' }]).map((input) => (
                    <li key={input.name}>{input.name}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h4>Outputs</h4>
                {node.outputs.length > 0 ? (
                  <ul className="cn-io-list">
                    {node.outputs.map((output) => (
                      <li key={output.name}>
                        <span>{output.name}</span>
                        {output.level ? <span className={`cn-level cn-level--${output.level}`}>{output.level}</span> : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="hint">None declared.</p>
                )}
              </div>
            </div>
            {node.routing ? (
              <div className="cn-routing">
                <h4>Routing</h4>
                <p>{node.routing}</p>
              </div>
            ) : null}
          </section>

          {node.changelog && node.changelog.length > 0 ? (
            <section className="card">
              <h2>Changelog</h2>
              <ul className="cn-changelog">
                {node.changelog.map((entry, index) => (
                  <li key={`${entry.version}-${index}`} className="cn-changelog__item">
                    <div className="cn-changelog__head">
                      <span className="cn-changelog__ver">v{entry.version}</span>
                      {entry.date ? <span className="cn-changelog__date">{entry.date}</span> : null}
                    </div>
                    {entry.notes ? <p className="cn-changelog__notes">{entry.notes}</p> : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {node.usageInstructions ? (
            <section className="card">
              <h2>Deployment</h2>
              <div className="cn-prose">{node.usageInstructions}</div>
            </section>
          ) : null}
        </div>

        <aside className="cn-detail-side">
          <section className="card card-subtle">
            <h3>Bundle</h3>
            <dl className="cn-meta">
              <dt>File</dt>
              <dd>{node.file ? node.file.fileName : '—'}</dd>
              <dt>Size</dt>
              <dd>{node.file ? formatBytes(node.file.size) : '—'}</dd>
            </dl>
          </section>

          <section className="card card-subtle">
            <h3>Visibility</h3>
            {node.visibility === 'public' ? (
              <p className="hint">Visible to everyone with access to the Custom Nodes tab.</p>
            ) : (
              <>
                <p className="hint">Limited to these teams:</p>
                {node.visibleTeams && node.visibleTeams.length > 0 ? (
                  <ul className="cn-team-list">
                    {node.visibleTeams.map((team) => (
                      <li key={team.id}>{team.name}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="hint">No teams selected.</p>
                )}
              </>
            )}
          </section>

          <section className="card card-subtle">
            <h3>Details</h3>
            <dl className="cn-meta">
              <dt>Shared by</dt>
              <dd>{node.createdByName || 'Unknown'}</dd>
              <dt>Updated</dt>
              <dd>{new Date(node.updatedAt).toLocaleString()}</dd>
            </dl>
          </section>
        </aside>
      </div>
    </div>
  );
}
